import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import ProgressWizard from 'components/field/ProgressWizard';
import Form from 'components/form/Form';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { OTO_LINK } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faAngleLeft, faAngleRight, faCheck, faPlus } from '@fortawesome/pro-regular-svg-icons';

import * as S from '../OTOLink.styled';
import AddBox from './AddBox';
import AddProduct from './AddProduct';
import LinkDetails from './LinkDetails';
import OTOLinkSuccess from './OTOLinkSuccess';

const AddProductAndBoxDetails = ({ mode }) => (
	<>
		<AddProduct mode={mode} />
		<AddBox mode={mode} />
		<LinkDetails mode={mode} />
	</>
);

const stages = [{ name: 'addProductAndBoxDetails' }, { name: 'success', success: true }];

const CreateOtoLink = NiceModal.create(({ id, mode }) => {
	// State
	const [tab, setTab] = useState(0);
	const [createdLink, setCreatedLink] = useState(false);

	// Hooks
	const modal = useModal();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { showError, showSuccess } = useAlert();
	const { showModal } = useModalRouter();
	const axios = useAxiosPrivate();
	const { Currency } = useLocale();
	const userData = useSelector((state) => state.user);

	// Selectors
	const user = useSelector((state) => state.user);
	const { settings: linkSettings } = useSelector((state) => state.otoLinkSettings);
	// Mode flags
	const addMode = mode !== 'edit' && mode !== 'view';
	const viewMode = mode === 'view';
	const editMode = mode === 'edit';
	// Tab configuration
	const tabs = [
		{
			label: 'addProductAndBoxDetails',
			component: () => <AddProductAndBoxDetails mode={mode} />,
			fields: ['items', 'boxes', 'linkExpiryCount', 'linkExpiryDate']
		},
		{
			label: 'success',
			component: () => <OTOLinkSuccess link={createdLink?.linkURL} />,
			fields: []
		}
	];

	const ActiveTab = useMemo(() => tabs[tab].component, [tab]);
	const modalTitle = useMemo(() => {
		if (addMode) return 'createOTOLink';
		if (viewMode) return 'viewOTOLink';
		return 'editOTOLink';
	}, [addMode, viewMode]);

	// Form configuration
	const defaultBox = useMemo(
		() => ({
			ID: user?.defaultBox?.id,
			width: user?.defaultBox?.width || 10,
			height: user?.defaultBox?.height || 10,
			length: user?.defaultBox?.length || 10,
			name: user?.defaultBox?.boxName,
			weight: 1
		}),
		[user]
	);

	const fillForm = useCallback(async () => {
		const defaultValues = {
			linkConfigurationID: linkSettings?.linkConfigID,
			linkExpiryCount: 1,
			linkExpiryDate: null,
			orderComments: null,
			items: [
				{
					productID: null,
					image: null,
					name: null,
					price: null,
					quantity: 1,
					total: null,
					tax: null
				}
			],
			boxes: [defaultBox],
			weight: 1,
			packageCount: 1
		};

		if (!id) return defaultValues;

		try {
			const response = await axios.post(OTO_LINK.GET_OTO_LINK, { otoLinkId: id });
			const { data, status } = response;

			if (status === 200) {
				return {
					...defaultValues,
					...data,
					total: data?.grandTotal
				};
			}

			AppLogger.error(response);
			showError();
			modal.remove();
		} catch (error) {
			AppLogger.error(error);
			showError();
			modal.remove();
		}
	}, []);

	const schema = yup.object().shape({
		linkConfigurationID: yup.number().required(),
		linkExpiryDate: yup.date().nullable(),
		...(addMode && {
			linkExpiryCount: yup.number().required().min(1),
			items: yup
				.array()
				.of(
					yup.object().shape({
						productID: yup.number().nullable(),
						name: yup.string().nullable().required(),
						quantity: yup.number().min(1).nullable().required(),
						price: yup.number().min(0).nullable().required(),
						description: yup.string().nullable(),
						total: yup.number().nullable(),
						image: yup.string().nullable()
					})
				)
				.required()
				.min(1),
			weight: yup.number().min(0.01).nullable().required(),
			packageCount: yup.number().min(1).nullable().required(),
			boxes: yup.array().of(
				yup.object().shape({
					ID: yup.number().nullable(),
					name: yup.string().nullable(),
					width: yup.number().positive().nullable().required(),
					height: yup.number().positive().nullable().required(),
					length: yup.number().positive().nullable().required(),
					weight: yup.number().positive().nullable().required(),
					image: yup.string().nullable()
				})
			)
		})
	});

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading },
		trigger,
		control
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const items = useWatch({ control, name: 'items' });

	const totalPrice = useMemo(() => items?.reduce((acc, item) => acc + item.total, 0), [items]);

	const onSubmit = async (values) => {
		try {
			const response = await axios.post(id ? OTO_LINK.UPDATE_OTO_LINK : OTO_LINK.ADD_OTO_LINK, {
				...values,
				otoLinkId: id
			});
			const { data, status } = response;
			if (status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.OTOLINK_ALL));
				showSuccess();
				setCreatedLink(data);

				if (!id) {
					window.dataLayer.push({
						/* eslint-disable camelcase */
						event: 'oto_link',
						first_name: userData?.firstName || '',
						last_name: userData?.lastName || '',
						phone: userData?.phone || '',
						email: userData?.email || '',
						plan: userData?.planName || '',
						country: userData?.localization?.label || '',
						userID: userData?.ID || ''
						/* eslint-enable camelcase */
					});
				}

				if (id) modal.remove();
				else setTab(1);
			} else {
				showError();
				AppLogger.error(data);
			}
		} catch (error) {
			AppLogger.error(error);
			if (error?.response?.data?.code === 'error.L019') {
				showError({
					header: 'error.L019'
				});
				showModal('warehouse-form');
				return;
			}
			showError({
				message: error?.response?.data?.code || error?.response?.data?.message
			});
		}
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	const nextStep = async () => {
		const currentTabFields = tabs[tab].fields;
		const isValid = await trigger(currentTabFields, {
			shouldFocus: true
		});
		if (isValid) {
			if (tab < tabs.length - 1) {
				setTab(tab + 1);
			}
		}
	};

	return (
		<Modal maxWidth="x-large" fullwidth className="modal-createOTOLink">
			<ModalTitle>
				<Typography variant="p1">{modalTitle}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					{viewMode && <Tabs data={tabs.filter((tab) => tab.fields.length > 0)} />}
					{addMode && (
						<>
							<ProgressWizard stages={stages} setIndex={setTab} index={tab} gutterBottom />
							{<ActiveTab mode={mode} />}
						</>
					)}
					{editMode && <LinkDetails mode={mode} />}
				</Form>
			</ModalContent>
			{!viewMode && (
				<ModalActions>
					<Button
						variant={isSubmitSuccessful ? 'blue' : 'blackOutline'}
						label={isSubmitSuccessful ? 'done' : 'cancel'}
						icon={isSubmitSuccessful && faCheck}
						onClick={modal.remove}
						css={
							!isSubmitSuccessful &&
							css`
								text-decoration: underline;
								border: none !important;
							`
						}
					/>
					{!addMode ? (
						<Button
							loading={isLoading}
							label="save"
							disabled={!isDirty}
							disabledTooltip={!isDirty && 'formInfo.noChanges'}
							onClick={handleSubmit(onSubmit, AppLogger.log)}
							id="CreateOtoLinkFormButton"
						/>
					) : (
						<>
							{tab !== 0 && !isSubmitSuccessful && (
								<Button label="previous" variant="outline" icon={faAngleLeft} onClick={prevStep} />
							)}
							{tab < tabs.length - 2 ? (
								<Button
									icon={faAngleRight}
									label="continue"
									iconPos="right"
									onClick={nextStep}
									variant="blue"
									loading={isLoading}
								/>
							) : null}
							{tab === tabs.length - 2 ? (
								<Button
									icon={totalPrice === 0 && faPlus}
									loading={isLoading}
									disabled={!isDirty || totalPrice === 0}
									disabledTooltip={!isDirty && 'formInfo.noChanges'}
									onClick={handleSubmit(onSubmit, AppLogger.error)}
									id="CreateOtoLinkFormButton">
									<Typography className="button-text" fontWeight="500" noWrap>
										<Trans
											i18nKey={totalPrice > 0 ? 'createOtoLinkButton' : 'createOTOLink'}
											values={{
												totalPrice,
												currency: t(Currency)
											}}
											components={{
												bold: <S.Bold />
											}}
										/>
									</Typography>

									{totalPrice > 0 && <Icon icon={faAngleRight} className="button-icon" />}
								</Button>
							) : null}
						</>
					)}
				</ModalActions>
			)}
		</Modal>
	);
});

export default CreateOtoLink;
